/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';
import MDDateRange from 'components/MDDateRange';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import { useParams } from 'react-router-dom';

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  period: 'ALL',
  branch: 'ALL',
  checklist_id: 'ALL',
  check_type_id: 'ALL',
  status: 'ALL',
  severity: 'ALL',
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

function RpProductChecklist() {
  const mod = 'rp_product_checklist';
  const { id } = useParams();
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [cboBranch, setCboBranch] = useState([]);
  const [cboCheckTypes, setCboCheckTypes] = useState([]);
  const [cboChecklists, setCboChecklists] = useState([]);
  const [urlExcel, setUrlExcel] = useState('');

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.id', label: t(`attr.image`), align: 'center', width: 40 },
    { id: 'pd.barcode', label: 'SKU', align: 'left' },
    { id: 'pd.name', label: 'สินค้า', align: 'left' },
    { id: 'tb.checklist_id', label: 'รายการตรวจสอบ', align: 'left' },
    { id: 'tb.check_type_id', label: 'ประเภทการตรวจ', align: 'center' },
    { id: 'tb.branch_id', label: 'สาขา', align: 'left' },
    { id: 'tb.severity', label: 'ระดับความสำคัญ', align: 'center', width: 120 },
    { id: 'tb.status', label: 'สถานะ', align: 'center', width: 100 },
    { id: 'tb.checked_by', label: 'ผู้ตรวจสอบ', align: 'left' },
    { id: 'tb.note', label: 'หมายเหตุ', align: 'left' },
    { id: 'tb.checked_at', label: 'วันที่ตรวจสอบ', align: 'center', width: 150 },
  ];

  const statusColors = {
    pending: 'warning',
    in_progress: 'info',
    done: 'success',
    failed: 'error',
  };

  const severityColors = {
    LOW: 'success',
    MEDIUM: 'warning',
    HIGH: 'error',
    CRITICAL: 'dark',
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    const requestBody = {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      branch_id: data.branch === 'ALL' ? undefined : data.branch,
      checklist_id: data.checklist_id === 'ALL' ? undefined : data.checklist_id,
      check_type_id: data.check_type_id === 'ALL' ? undefined : data.check_type_id,
      status: data.status === 'ALL' ? undefined : data.status,
      severity: data.severity === 'ALL' ? undefined : data.severity,
    };

    if (data.period !== 'ALL') {
      requestBody.date_from = data.date_start;
      requestBody.date_to = data.date_stop;
    }

    FetchData('POST', `/${mod}`, app.token, requestBody).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data || []);
        setPageTotal(result.total_pages || 0);
        setPageNo(result.page || 1);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    let param = `download`;
    param += `?search=${find.search}`;
    param += find.branch !== 'ALL' ? `&branch_id=${find.branch}` : '';
    param += find.checklist_id !== 'ALL' ? `&checklist_id=${find.checklist_id}` : '';
    param += find.check_type_id !== 'ALL' ? `&check_type_id=${find.check_type_id}` : '';
    param += find.status !== 'ALL' ? `&status=${find.status}` : '';
    param += find.severity !== 'ALL' ? `&severity=${find.severity}` : '';
    param += find.period === 'ALL' ? '' : `&date_from=${find.date_start}`;
    param += find.period === 'ALL' ? '' : `&date_to=${find.date_stop}`;

    window.open(`${urlExcel}/${param}`, '_self');
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 50 })}>
        {t(`element.show_record`, { num: 50 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.checklist_id}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, checklist_id: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: <InputAdornment position='start'>รายการตรวจ :</InputAdornment>,
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboChecklists.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.code} - {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.check_type_id}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, check_type_id: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: <InputAdornment position='start'>ประเภท :</InputAdornment>,
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboCheckTypes.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.status}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, status: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: <InputAdornment position='start'>สถานะ :</InputAdornment>,
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='pending'>รอดำเนินการ</MenuItem>
              <MenuItem value='in_progress'>กำลังดำเนินการ</MenuItem>
              <MenuItem value='done'>เสร็จสิ้น</MenuItem>
              <MenuItem value='failed'>ไม่ผ่าน</MenuItem>
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.severity}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, severity: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: <InputAdornment position='start'>ความสำคัญ :</InputAdornment>,
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='LOW'>ต่ำ</MenuItem>
              <MenuItem value='MEDIUM'>ปานกลาง</MenuItem>
              <MenuItem value='HIGH'>สูง</MenuItem>
              <MenuItem value='CRITICAL'>วิกฤต</MenuItem>
            </MDInput>
          </Grid>

          {find.period === 'ALL' ? (
            <Grid item xs={12} sm={12} md={6} lg={2}>
              <MDButton
                variant='outlined'
                color='secondary'
                fullWidth
                sx={{ p: 1.5 }}
                onClick={() => view({ ...find, period: 'DATE', page: 1 })}
              >
                <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
              </MDButton>
            </Grid>
          ) : null}
          {find.period === 'ALL' ? null : (
            <>
              <Grid item xs={12} sm={12} md={12} lg={3}>
                <MDDateRange
                  dateStart={find.date_start}
                  dateStop={find.date_stop}
                  callBack={setDateRange}
                  closeDate={() => view({ ...find, period: 'ALL', page: 1 })}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={1}>
                <MDBox display='flex' justifyContent='end' gap={1}>
                  <MDButton variant='gradient' color='success' onClick={excel}>
                    <Icon>table_view</Icon>&nbsp;Excel
                  </MDButton>
                </MDBox>
              </Grid>
            </>
          )}
        </Grid>
      </Card>
    );
  };

  const renderStatusChip = (status) => {
    const statusLabels = {
      pending: 'รอดำเนินการ',
      in_progress: 'กำลังดำเนินการ',
      done: 'เสร็จสิ้น',
      failed: 'ไม่ผ่าน',
    };

    return (
      <Chip
        label={statusLabels[status] || status}
        color={statusColors[status] || 'default'}
        size='small'
      />
    );
  };

  const renderSeverityChip = (severity) => {
    const severityLabels = {
      LOW: 'ต่ำ',
      MEDIUM: 'ปานกลาง',
      HIGH: 'สูง',
      CRITICAL: 'วิกฤต',
    };

    return (
      <Chip
        label={severityLabels[severity] || severity}
        color={severityColors[severity] || 'default'}
        size='small'
      />
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_account'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{Func.imageDisplay(row)}</TableCell>
                      <TableCell align='left'>{row.barcode}</TableCell>
                      <TableCell align='left'>
                        <MDBox>{row.product_code}</MDBox>
                        <MDBox className='nowrap'>{row.product_name}</MDBox>
                      </TableCell>
                      <TableCell align='left'>
                        <MDBox className='nowrap'>{row.checklist_code}</MDBox>
                        <MDBox className='nowrap'>{row.checklist_name}</MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        <MDBox className='nowrap'>{row.check_type_name}</MDBox>
                      </TableCell>
                      <TableCell align='left'>
                        <MDBox className='nowrap'>{row.branch_name}</MDBox>
                      </TableCell>
                      <TableCell align='center'>{renderSeverityChip(row.severity)}</TableCell>
                      <TableCell align='center'>{renderStatusChip(row.status)}</TableCell>
                      <TableCell align='left'>{row.checked_by_name}</TableCell>
                      <TableCell align='left'>
                        <MDBox className='text-truncate' sx={{ maxWidth: 200 }}>
                          {row.note || '-'}
                        </MDBox>
                      </TableCell>
                      <TableCell align='center' className='nowrap'>
                        {Func.dateTimeDisplay(row.checked_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    const init = async () => {
      const res = await FetchData('GET', `/${mod}`, app.token, {
        lang: app.lang,
      });

      if (res.status === 'ok') {
        setCboBranch(res.branch || []);
        setCboCheckTypes(res.check_types || []);
        setCboChecklists(res.checklists || []);
        setUrlExcel(res.excel || '');
        view({ ...find });
      } else {
        Func.exitApp();
      }
    };

    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.account`), 'รายงานการตรวจสอบสินค้า']} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpProductChecklist;
